import type {
  BaseCheckCoverage,
  BaseCheckDiagnostic,
  BaseCheckField,
  BaseCheckInstance,
  BaseCheckQuestion,
  BaseCheckResult,
} from "types/dataChecks.types"

/**
 * Pure formatting and bucketing for the base-check report. No React — everything here is a
 * decision about what a number MEANS, which is exactly the part worth unit-testing.
 *
 * The rules encoded here come from the report contract's gates (`types/dataChecks.types.ts`);
 * where one of them is load-bearing the comment names it.
 */

/**
 * GATE — `coverage` says whether tab specs can base a table on who was asked, NOT whether the
 * question was checked. Every question in the report was walked, so coverage never moves a
 * question between buckets; it is shown beside the question as a note about its tables.
 */
export const hasTabSpecCondition = (coverage: BaseCheckCoverage): boolean =>
  coverage === "full"

export type BaseCheckBucket = "offPath" | "baseGaps" | "undetermined" | "clean"

/** Display order of the buckets — also the membership priority (see {@link bucketQuestions}). */
export const BASE_CHECK_BUCKET_ORDER: readonly BaseCheckBucket[] = [
  "offPath",
  "baseGaps",
  "undetermined",
  "clean",
] as const

export type BaseCheckBuckets = Record<BaseCheckBucket, BaseCheckQuestion[]>

/**
 * Splits the report's questions into the four buckets.
 *
 * MEMBERSHIP RULE — a question appears in exactly ONE bucket: the first it qualifies for, in the
 * order above, with `clean` as the fallback. Per-bucket counts are therefore the bucket lengths
 * and they sum to the number of questions. A question with off-path answers AND undetermined
 * respondents leads with the off-path finding (the actionable one); the rest of its picture is
 * on its detail. That is a display priority, not a claim that the two sets are apart: within one
 * instance a respondent can be both.
 *
 * Questions are ordered by `ordinal` inside each bucket so the report reads in questionnaire
 * order whatever order the wire happened to use.
 */
export const bucketQuestions = (report: BaseCheckResult): BaseCheckBuckets => {
  const buckets: BaseCheckBuckets = {
    offPath: [],
    baseGaps: [],
    undetermined: [],
    clean: [],
  }

  for (const question of report.questions) {
    if (question.offPath > 0) buckets.offPath.push(question)
    else if (question.missingOnPath > 0) buckets.baseGaps.push(question)
    else if (question.undetermined > 0) buckets.undetermined.push(question)
    else buckets.clean.push(question)
  }

  for (const key of BASE_CHECK_BUCKET_ORDER) {
    buckets[key].sort((a, b) => a.ordinal - b.ordinal)
  }

  return buckets
}

/** Questions whose asked condition cannot be shown to tab specs. */
export const questionsWithoutTabSpecCondition = (
  report: BaseCheckResult
): BaseCheckQuestion[] =>
  report.questions.filter((question) => !hasTabSpecCondition(question.coverage))

/** Tailwind chip classes, one per tone. */
export const TONE = {
  rose: "border-rose-200 bg-rose-50 text-rose-700",
  amber: "border-amber-200 bg-amber-50 text-amber-700",
  violet: "border-violet-200 bg-violet-50 text-violet-700",
  sky: "border-sky-200 bg-sky-50 text-sky-700",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
} as const

export type BaseCheckTone = keyof typeof TONE

/**
 * Off-path is the finding (rose); base gaps are advisory (amber); undetermined is an unknown
 * (violet); clean is clean (emerald).
 */
export const toneForBucket = (bucket: BaseCheckBucket): BaseCheckTone => {
  switch (bucket) {
    case "offPath":
      return "rose"
    case "baseGaps":
      return "amber"
    case "undetermined":
      return "violet"
    case "clean":
      return "emerald"
  }
}

/** Heading and one-line reading for each bucket. The wording is the honesty story — keep it. */
export const BUCKET_COPY: Record<BaseCheckBucket, { label: string; hint: string }> = {
  offPath: {
    label: "Off-path answers",
    hint: "answered although the routing did not ask — the set the cleaning stage can act on",
  },
  baseGaps: {
    label: "Base gaps",
    hint: "asked and left blank. Not cleaned — a base-size observation, not stale data.",
  },
  undetermined: {
    label: "Undetermined",
    hint: "a gating answer is blank or can't be read, so we cannot tell whether they were asked. These are never cleaned.",
  },
  clean: {
    label: "Clean",
    hint: "nothing off path, nothing missing, nothing undetermined",
  },
}

/**
 * What coverage means for a question's tables, in words. Coverage is about tab specs only:
 * the check itself walked every question whatever its coverage.
 */
export const formatCoverageLabel = (coverage: BaseCheckCoverage): string => {
  switch (coverage) {
    case "full":
      return "asked condition available to tab specs"
    case "unsupported":
      return "asked condition can't be shown to tab specs — its tables use everyone who answered"
    // Never sent by the walker; kept so an older server's answer still reads.
    case "partial":
      return "asked condition only partly available to tab specs"
    case "failed":
      return "asked condition could not be worked out for tab specs"
  }
}

/** The chip tone for a coverage value: informational, never a finding. */
export const toneForCoverage = (coverage: BaseCheckCoverage): BaseCheckTone =>
  hasTabSpecCondition(coverage) ? "emerald" : "sky"

export interface DiagnosticSummary {
  code: string
  severity: BaseCheckDiagnostic["severity"]
  /** The first message seen for this code — `count` says how many carried it. */
  message: string
  count: number
  /** The distinct questions this code was raised on; empty for report-level diagnostics. */
  questions: string[]
}

const SEVERITY_RANK: Record<BaseCheckDiagnostic["severity"], number> = {
  info: 0,
  warning: 1,
  error: 2,
}

/**
 * Dedupes diagnostics by `code`, keeping first-seen order and the first message. Where one code
 * arrives at more than one severity the highest is kept — understating a diagnostic is the one
 * failure mode worth avoiding here.
 */
export const summariseDiagnostics = (
  diagnostics: BaseCheckDiagnostic[]
): DiagnosticSummary[] => {
  const byCode = new Map<string, DiagnosticSummary>()

  for (const diagnostic of diagnostics) {
    const seen = byCode.get(diagnostic.code)
    if (!seen) {
      byCode.set(diagnostic.code, {
        code: diagnostic.code,
        severity: diagnostic.severity,
        message: diagnostic.message,
        count: 1,
        questions: diagnostic.question ? [diagnostic.question] : [],
      })
      continue
    }
    seen.count += 1
    if (SEVERITY_RANK[diagnostic.severity] > SEVERITY_RANK[seen.severity]) {
      seen.severity = diagnostic.severity
    }
    if (diagnostic.question && !seen.questions.includes(diagnostic.question)) {
      seen.questions.push(diagnostic.question)
    }
  }

  return [...byCode.values()]
}

/** Report-level lookup for the codes the contract calls out by name. */
export const diagnosticByCode = (
  diagnostics: BaseCheckDiagnostic[],
  code: string
): BaseCheckDiagnostic | undefined => diagnostics.find((d) => d.code === code)

/**
 * A field's off-path figure, or the words that stand in for it.
 *
 * GATE — `countable` governs. An uncountable field's `offPath` is not a measurement, so it is
 * never printed: a zero there is the absence of a count, not a clean bill.
 */
export const formatFieldOffPath = (field: BaseCheckField): string =>
  field.countable ? field.offPath.toLocaleString() : "not counted"

/**
 * The loop cell an instance belongs to, e.g. `Coca-Cola › Last week` (outer loop first).
 * Empty for an unlooped question.
 */
export const formatIteration = (iteration: string[]): string => iteration.join(" › ")

/** What `alwaysAsked` means on this instance, or nothing when it has a condition. */
export const describeAlwaysAsked = (instance: BaseCheckInstance): string | undefined =>
  instance.alwaysAsked ? "asked of everyone — no condition to break" : undefined

/**
 * The example list's caption, with the cap stated inline.
 *
 * The ids are respondents; `offPath` counts ANSWERS (summed over a looped question's instances),
 * so both units are named rather than implying one respondent per answer.
 */
export const formatExampleCaption = (question: BaseCheckQuestion): string =>
  question.examplesCapped
    ? `Showing ${question.examples.length} respondent ids of ${question.offPath} off-path answers — the list is capped at ${question.exampleCap}.`
    : `Showing all ${question.examples.length} respondent ids with an off-path answer here — the cap is ${question.exampleCap}.`

/**
 * Why the example lists are empty when `examplesAvailable` is false.
 *
 * GATE — say WHY. Unexplained, an empty list reads as "no respondent violates these questions",
 * which is the opposite of what the counts say.
 */
export const explainExamplesUnavailable = (
  diagnostics: BaseCheckDiagnostic[]
): string => {
  const reason =
    diagnosticByCode(diagnostics, "respondent_ids_unavailable") ??
    diagnosticByCode(diagnostics, "respondent_ids_unread")
  return (
    reason?.message ??
    "No respondent ids came back, and the report did not say why. An empty list here is not evidence that nobody answered off path."
  )
}
